/**
 * InboxBulkActionBar — bulk-selection toolbar for the Conversazioni hub.
 *
 * Thin domain wrapper around :class:`BulkActionStrip` (`@ando/ui`): the
 * primitive owns the sticky-bottom chrome + count badge + dismiss
 * affordance, this file declares the inbox-specific action vocabulary
 * (``Segna letta`` / ``Snooze`` / ``Stella`` / ``Archivia``).
 *
 * ``Elimina`` is NOT exposed — archive is the destructive intent for
 * email. PEC + chat rows that can't be archived are filtered out by
 * the parent (``archiveMany``) so the user gets a clear toast instead
 * of a silent partial action.
 */

import { Archive, Clock, MailOpen, Star } from "lucide-react";
import type * as React from "react";
import { useLingui } from "@lingui/react/macro";

import { BulkActionStrip, BulkActionStripButton } from "@ando/ui";

import { SnoozePopover } from "./SnoozePopover";


export interface InboxBulkActionBarProps {
  readonly count: number;
  readonly onDeselectAll: () => void;
  readonly onMarkRead?: () => void;
  readonly onSnooze?: (epochMs: number) => void;
  readonly onArchive?: () => void;
  readonly onToggleStar?: () => void;
  readonly className?: string;
}


export const InboxBulkActionBar: React.FC<InboxBulkActionBarProps> = ({
  count,
  onDeselectAll,
  onMarkRead,
  onSnooze,
  onArchive,
  onToggleStar,
  className,
}) => {
  const { t } = useLingui();
  return (
    <BulkActionStrip
      count={count}
      onDeselectAll={onDeselectAll}
      ariaLabel={t`Azioni in blocco — Conversazioni`}
      className={className}
    >
      <BulkActionStripButton
        icon={<MailOpen size={13} strokeWidth={2} aria-hidden />}
        label={t`Segna letta`}
        onClick={onMarkRead}
      />
      {onSnooze ? (
        <SnoozePopover onPick={onSnooze}>
          <BulkActionStripButton
            icon={<Clock size={13} strokeWidth={2} aria-hidden />}
            label={t`Snooze`}
            onClick={() => {
              /* popover-handled */
            }}
          />
        </SnoozePopover>
      ) : null}
      <BulkActionStripButton
        icon={<Star size={13} strokeWidth={2} aria-hidden />}
        label={t`Stella`}
        onClick={onToggleStar}
      />
      <BulkActionStripButton
        icon={<Archive size={13} strokeWidth={2} aria-hidden />}
        label={t`Archivia`}
        tone="destructive"
        onClick={onArchive}
      />
    </BulkActionStrip>
  );
};
InboxBulkActionBar.displayName = "InboxBulkActionBar";
